import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Vote, Plus, RefreshCw, Users, AlertTriangle, Trophy, UserPlus, Clock,
  Trash2, CheckCircle2, ChevronRight, ClipboardCheck, TrendingUp,
} from "lucide-react";

const STATUS_TONE = {
  active:            "border-emerald-500/30 bg-emerald-500/[0.05] text-emerald-300",
  expiring:          "border-amber-500/40 bg-amber-500/[0.06] text-amber-300",
  expired:           "border-red-500/40 bg-red-500/[0.06] text-red-300",
  up_for_election:   "border-sky-500/40 bg-sky-500/[0.06] text-sky-300",
  vacant:            "border-zinc-500/40 bg-zinc-500/[0.06] text-zinc-300",
};

const STATUS_LABEL = {
  active: "Active",
  expiring: "Expiring < 90d",
  expired: "Expired",
  up_for_election: "Election open",
  vacant: "Vacant",
};

function KPI({ label, value, testid }) {
  return (
    <Card className="p-4 border-white/10 bg-card" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold text-white mt-1 font-display">{value}</div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Add-seat dialog
// -----------------------------------------------------------------------------
function AddSeatDialog({ onCreated, propertyId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    seat_key: "", seat_title: "", term_class: "I", term_length_years: 3,
    term_start: new Date().toISOString().slice(0, 10),
    member_name: "", member_email: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!propertyId) return toast.error("Pick a property first.");
    setBusy(true);
    try {
      await api.post("/admin/board-terms", { ...form, property_id: propertyId });
      toast.success("Seat added");
      setOpen(false);
      setForm((f) => ({ ...f, seat_key: "", seat_title: "", member_name: "", member_email: "" }));
      onCreated();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to add seat");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="add-seat-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add seat
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white max-w-lg" data-testid="add-seat-dialog">
        <DialogHeader><DialogTitle>New board seat</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Seat key (unique)</Label>
            <Input value={form.seat_key}
                   onChange={(e) => setForm({ ...form, seat_key: e.target.value })}
                   placeholder="president"
                   className="bg-white/[0.04] border-white/10" data-testid="seat-key-input" />
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.seat_title}
                   onChange={(e) => setForm({ ...form, seat_title: e.target.value })}
                   placeholder="Board President"
                   className="bg-white/[0.04] border-white/10" data-testid="seat-title-input" />
          </div>
          <div>
            <Label className="text-xs">Class (staggered)</Label>
            <Select value={form.term_class} onValueChange={(v) => setForm({ ...form, term_class: v })}>
              <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="seat-class-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                <SelectItem value="I">Class I</SelectItem>
                <SelectItem value="II">Class II</SelectItem>
                <SelectItem value="III">Class III</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Term length (yrs)</Label>
            <Input type="number" min={1} max={5} value={form.term_length_years}
                   onChange={(e) => setForm({ ...form, term_length_years: Number(e.target.value) })}
                   className="bg-white/[0.04] border-white/10" />
          </div>
          <div>
            <Label className="text-xs">Term start</Label>
            <Input type="date" value={form.term_start}
                   onChange={(e) => setForm({ ...form, term_start: e.target.value })}
                   className="bg-white/[0.04] border-white/10" />
          </div>
          <div>
            <Label className="text-xs">Incumbent (optional)</Label>
            <Input value={form.member_name}
                   onChange={(e) => setForm({ ...form, member_name: e.target.value })}
                   placeholder="Jane Board"
                   className="bg-white/[0.04] border-white/10" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Incumbent email (optional)</Label>
            <Input value={form.member_email}
                   onChange={(e) => setForm({ ...form, member_email: e.target.value })}
                   placeholder="jane@example.com"
                   className="bg-white/[0.04] border-white/10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
          <Button disabled={busy || !form.seat_key || !form.seat_title} onClick={submit}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="seat-submit">
            {busy ? "Saving…" : "Add seat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Election dialog — one dialog handles nominate + vote + finalize
// -----------------------------------------------------------------------------
function ElectionDialog({ term, onChanged }) {
  const [open, setOpen] = useState(false);
  const [nomineeForm, setNomineeForm] = useState({ name: "", email: "", statement: "" });
  const [busy, setBusy] = useState(false);
  const e = term.election;

  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/board-terms/${term.id}/start-election`,
                     { nominations_days: 14, voting_days: 7 });
      toast.success("Election opened");
      onChanged();
    } catch (er) { toast.error(apiErrorMessage(er) || "Failed"); }
    finally { setBusy(false); }
  };
  const nominate = async () => {
    if (!nomineeForm.name) return;
    setBusy(true);
    try {
      await api.post(`/admin/board-terms/${term.id}/nominate`, nomineeForm);
      toast.success("Candidate nominated");
      setNomineeForm({ name: "", email: "", statement: "" });
      onChanged();
    } catch (er) { toast.error(apiErrorMessage(er) || "Failed"); }
    finally { setBusy(false); }
  };
  const vote = async (candidate_id) => {
    setBusy(true);
    try {
      await api.post(`/admin/board-terms/${term.id}/vote`, { candidate_id });
      toast.success("Vote recorded");
      onChanged();
    } catch (er) { toast.error(apiErrorMessage(er) || "Failed"); }
    finally { setBusy(false); }
  };
  const finalize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/board-terms/${term.id}/finalize-election`);
      toast.success(`Winner: ${data.winner.name}`);
      onChanged();
    } catch (er) { toast.error(apiErrorMessage(er) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-sky-500/30 text-sky-300"
                data-testid={`election-btn-${term.id}`}>
          <Vote className="h-3 w-3 mr-1" />
          {e ? `Election (${e.status})` : "Start election"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{term.seat_title} — Election</DialogTitle>
        </DialogHeader>
        {!e ? (
          <div className="text-sm text-zinc-400">
            <p>Opening nominations kicks off a 14-day nomination window followed by
              a 7-day voting window. Board members cast one vote each.</p>
            <div className="mt-4">
              <Button disabled={busy} onClick={start} className="bg-orange-500 hover:bg-orange-600 rounded-full">
                <Vote className="h-3.5 w-3.5 mr-1" /> Open nominations
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-xs text-zinc-400 flex flex-wrap gap-3">
              <span>Status: <span className="text-white uppercase">{e.status}</span></span>
              <span>Nominations end: {(e.nominations_end_at || "").slice(0, 10)}</span>
              <span>Voting ends: {(e.voting_ends_at || "").slice(0, 10)}</span>
            </div>
            <div className="rounded-md border border-white/10 overflow-hidden">
              <div className="text-[11px] text-zinc-500 px-3 py-2 bg-white/[0.03]">Candidates ({e.candidates?.length || 0})</div>
              {(e.candidates || []).length === 0 ? (
                <div className="p-3 text-xs text-zinc-500">No candidates yet.</div>
              ) : (
                (e.candidates || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between border-t border-white/5 px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{c.name}</div>
                      {c.statement && <div className="text-[11px] text-zinc-500 truncate">{c.statement}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="border-white/10 text-zinc-300">{c.votes} vote{c.votes === 1 ? "" : "s"}</Badge>
                      {e.status !== "decided" && (
                        <Button size="sm" variant="outline" className="border-white/10 h-7 px-2 text-[11px]"
                                disabled={busy} onClick={() => vote(c.id)} data-testid={`vote-${c.id}`}>
                          Vote
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            {e.status !== "decided" && (
              <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">Add candidate</div>
                <Input placeholder="Full name" value={nomineeForm.name}
                       onChange={(ev) => setNomineeForm({ ...nomineeForm, name: ev.target.value })}
                       className="bg-white/[0.04] border-white/10" data-testid="nominee-name" />
                <Input placeholder="Email (optional)" value={nomineeForm.email}
                       onChange={(ev) => setNomineeForm({ ...nomineeForm, email: ev.target.value })}
                       className="bg-white/[0.04] border-white/10" />
                <Textarea placeholder="Candidate statement (optional)" rows={2}
                          value={nomineeForm.statement}
                          onChange={(ev) => setNomineeForm({ ...nomineeForm, statement: ev.target.value })}
                          className="bg-white/[0.04] border-white/10" />
                <div className="flex gap-2 justify-end">
                  <Button disabled={busy || !nomineeForm.name} onClick={nominate}
                          className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="nominate-btn">
                    <UserPlus className="h-3 w-3 mr-1" /> Nominate
                  </Button>
                </div>
              </div>
            )}
            {e.status !== "decided" && (e.candidates || []).length > 0 && (
              <div className="flex justify-end">
                <Button disabled={busy} onClick={finalize} variant="outline"
                        className="border-emerald-500/30 text-emerald-300" data-testid="finalize-election">
                  <Trophy className="h-3.5 w-3.5 mr-1" /> Finalize + declare winner
                </Button>
              </div>
            )}
            {e.status === "decided" && e.winner && (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] p-3">
                <div className="flex items-center gap-2 text-emerald-300">
                  <Trophy className="h-4 w-4" />
                  <div className="font-semibold">Winner: {e.winner.name}</div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">Seat renewed automatically.</div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// R108: Attendance compliance dialog — per-seat attendance % over the current
// term window with meeting-by-meeting breakdown.
// -----------------------------------------------------------------------------
function AttendanceComplianceDialog({ term }) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/admin/board-terms/${term.id}/attendance-report`)
      .then(({ data }) => setReport(data))
      .catch((e) => toast.error(apiErrorMessage(e) || "Failed to load compliance"))
      .finally(() => setLoading(false));
  }, [open, term.id]);
  const pct = report?.attendance_pct;
  const meets = report?.meets_threshold;

  const exportCsv = () => {
    if (!report) return;
    // Client-side CSV so we don't need a new backend endpoint just to
    // format 20 rows — admins download exactly what's on screen.
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const header = ["Seat", "Member", "Term start", "Term end", "Threshold %", "Attendance %",
                     "Present", "Proxy", "Excused", "Absent", "Meetings in window"];
    const summary = [
      esc(report.seat_title), esc(report.member_name),
      esc(report.term_start), esc(report.term_end),
      esc(report.threshold_pct), esc(report.attendance_pct == null ? "n/a" : report.attendance_pct),
      esc(report.present), esc(report.proxy), esc(report.excused), esc(report.absent),
      esc(report.meetings_in_window),
    ].join(",");
    const rowHeader = ["", "Meeting", "Date", "Status"].join(",");
    const rows = (report.meetings || []).map((m) => [
      "", esc(m.title), esc((m.scheduled_at || "").slice(0, 10)), esc(m.status),
    ].join(","));
    const csv = [header.join(","), summary, "", rowHeader, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${(report.seat_key || term.id).replace(/[^a-z0-9-]/gi, "_")}-${(report.term_start || "").slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Attendance report downloaded");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-indigo-500/30 text-indigo-300 h-7 px-2 text-[11px]"
                data-testid={`compliance-${term.id}`}>
          <ClipboardCheck className="h-3 w-3 mr-1" /> Attendance
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="attendance-compliance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span>Attendance compliance · {term.seat_title}</span>
            {report && (
              <Button size="sm" variant="outline" className="border-orange-500/30 text-orange-300 h-7 px-2 text-[11px]"
                       onClick={exportCsv} data-testid="compliance-export-csv">
                <Download className="h-3 w-3 mr-1" /> Export CSV
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        {loading || !report ? (
          <div className="p-6 text-center text-sm text-zinc-500">Loading report…</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className={`h-5 w-5 ${meets ? "text-emerald-300" : "text-rose-300"}`} />
                <div>
                  <div className="text-3xl font-semibold" data-testid="compliance-pct">
                    {pct == null ? "—" : `${pct}%`}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    attended · target ≥ {report.threshold_pct}%
                    {meets ? " · on track" : (pct != null ? " · below threshold" : "")}
                  </div>
                </div>
                <Badge variant="outline" className={
                  pct == null ? "border-zinc-500/30 text-zinc-400 ml-auto"
                  : meets ? "border-emerald-500/30 text-emerald-300 ml-auto"
                  : "border-rose-500/30 text-rose-300 ml-auto"
                }>
                  {pct == null ? "No data" : (meets ? "Compliant" : "At risk")}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center text-[11px]">
              <div className="rounded border border-emerald-500/20 bg-emerald-500/[0.04] py-2">
                <div className="text-lg font-semibold text-emerald-300">{report.present}</div>
                <div className="text-zinc-500">present</div>
              </div>
              <div className="rounded border border-indigo-500/20 bg-indigo-500/[0.04] py-2">
                <div className="text-lg font-semibold text-indigo-300">{report.proxy}</div>
                <div className="text-zinc-500">proxy</div>
              </div>
              <div className="rounded border border-amber-500/20 bg-amber-500/[0.04] py-2">
                <div className="text-lg font-semibold text-amber-300">{report.excused}</div>
                <div className="text-zinc-500">excused</div>
              </div>
              <div className="rounded border border-zinc-500/20 bg-zinc-500/[0.03] py-2">
                <div className="text-lg font-semibold text-zinc-300">{report.absent}</div>
                <div className="text-zinc-500">absent</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Meetings in term ({report.meetings_in_window})
              </div>
              <div className="max-h-56 overflow-y-auto rounded border border-white/10 divide-y divide-white/5" data-testid="compliance-meetings">
                {(report.meetings || []).map((m) => (
                  <div key={m.meeting_id} className="flex items-center justify-between px-2 py-1.5 text-[11px]" data-testid={`compliance-meeting-${m.meeting_id}`}>
                    <div className="min-w-0">
                      <div className="text-zinc-200 truncate">{m.title}</div>
                      <div className="text-[10px] text-zinc-500">{(m.scheduled_at || "").slice(0, 10)}</div>
                    </div>
                    <Badge variant="outline" className={
                      m.status === "present" ? "border-emerald-500/30 text-emerald-300 text-[10px]"
                      : m.status === "proxy" ? "border-indigo-500/30 text-indigo-300 text-[10px]"
                      : m.status === "excused" ? "border-amber-500/30 text-amber-300 text-[10px]"
                      : "border-zinc-500/30 text-zinc-400 text-[10px]"
                    }>{m.status}</Badge>
                  </div>
                ))}
                {(report.meetings || []).length === 0 && (
                  <div className="p-3 text-center text-zinc-500 text-[11px]">No meetings recorded in this term window.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
export default function BoardTermsPage() {
  const { propertyId } = useProperty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = propertyId ? { property_id: propertyId } : {};
      const { data } = await api.get("/admin/board-terms", { params });
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load board terms");
    } finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const renew = async (id) => {
    try {
      await api.post(`/admin/board-terms/${id}/renew`);
      toast.success("Term renewed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this seat?")) return;
    try {
      await api.delete(`/admin/board-terms/${id}`);
      toast.success("Seat removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  const k = data?.kpis || {};
  const by = data?.by_class || { I: [], II: [], III: [] };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Governance"
        title="Board Term Tracker"
        subtitle="Staggered elections + auto-nudges 90 and 30 days before every seat expires."
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-white/10"
                    onClick={load} data-testid="terms-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <AddSeatDialog onCreated={load} propertyId={propertyId} />
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Total seats" value={k.total || 0} testid="term-kpi-total" />
        <KPI label="Active" value={k.active || 0} testid="term-kpi-active" />
        <KPI label="Expiring < 90d" value={k.expiring_soon || 0} testid="term-kpi-expiring" />
        <KPI label="Up for election" value={k.up_for_election || 0} testid="term-kpi-election" />
        <KPI label="Expired / vacant" value={(k.expired || 0) + (k.vacant || 0)} testid="term-kpi-vacant" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {["I", "II", "III"].map((cls) => {
          const rows = by[cls] || [];
          return (
            <div key={cls} className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
                 data-testid={`term-class-${cls}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-white">Class {cls}</div>
                <Badge variant="outline" className="border-white/10 text-zinc-300">{rows.length}</Badge>
              </div>
              {rows.length === 0 ? (
                <div className="text-xs text-zinc-500 text-center py-6">No seats in this class.</div>
              ) : (
                rows.map((t) => (
                  <Card key={t.id} className="p-3 mb-2 border-white/10 bg-card" data-testid={`term-card-${t.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{t.seat_title}</div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {t.member_name || <em className="text-amber-300">vacant</em>}
                        </div>
                      </div>
                      <Badge variant="outline" className={STATUS_TONE[t.status] || STATUS_TONE.active}>
                        {STATUS_LABEL[t.status] || t.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-zinc-400 mt-2">
                      <Clock className="h-3 w-3" />
                      Term end: {t.term_end}
                      <span className={t.days_until_end != null && t.days_until_end < 90
                        ? "text-amber-300" : "text-zinc-500"}>
                        {t.days_until_end == null
                          ? ""
                          : t.days_until_end < 0
                            ? ` (${Math.abs(t.days_until_end)}d overdue)`
                            : ` (${t.days_until_end}d)`}
                      </span>
                    </div>
                    {t.status === "expiring" && (
                      <div className="flex items-center gap-2 text-[11px] text-amber-300 mt-2">
                        <AlertTriangle className="h-3 w-3" /> Renew or start an election.
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/5">
                      <ElectionDialog term={t} onChanged={load} />
                      <AttendanceComplianceDialog term={t} />
                      <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-300 h-7 px-2 text-[11px]"
                              onClick={() => renew(t.id)} data-testid={`renew-${t.id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Renew term
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/30 text-red-300 h-7 px-2 text-[11px]"
                              onClick={() => del(t.id)} data-testid={`delete-${t.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Remove
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          );
        })}
      </div>

      {!loading && (data?.items?.length || 0) === 0 && (
        <div className="text-center text-sm text-zinc-500 py-8" data-testid="terms-empty">
          <Users className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
          No board seats yet. Click <ChevronRight className="inline h-3 w-3" /> Add seat to get started.
        </div>
      )}
    </div>
  );
}
